import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Shield, User as UserIcon, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function Users() {
  const { user: me, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', user}
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-3xl animate-fade-up">
      <PageHeader overline="Yönetim" title="Kullanıcılar" subtitle="Uygulamaya erişimi olan kullanıcılar">
        {isAdmin && (
          <Button onClick={() => setDialog({ mode: "create" })} data-testid="add-user-button">
            <UserPlus className="mr-2 h-4 w-4" /> Kullanıcı Ekle
          </Button>
        )}
      </PageHeader>

      <div className="divide-y divide-border rounded-md border border-border bg-card" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between px-4 py-3" data-testid={`user-${u.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {u.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">{u.name} {u.id === me?.id && <span className="text-xs text-muted-foreground">(siz)</span>}</p>
                <p className="text-xs text-muted-foreground">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={u.role === "admin" ? "default" : "secondary"} className="gap-1">
                {u.role === "admin" ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                {u.role === "admin" ? "Yönetici" : "Kullanıcı"}
              </Badge>
              {isAdmin && (
                <>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ mode: "edit", user: u })} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  {u.id !== me?.id && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isAdmin && <p className="mt-4 text-sm text-muted-foreground">Kullanıcı yönetimi yalnızca yöneticiler tarafından yapılabilir.</p>}

      {dialog && <UserDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı sil?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" kullanıcısı silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-user"
              onClick={async (e) => {
                e.preventDefault();
                try { await api.delete(`/users/${toDelete.id}`); toast.success("Silindi"); setToDelete(null); load(); }
                catch (err) { toast.error(apiError(err.response?.data?.detail)); setToDelete(null); }
              }}
            >Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserDialog({ dialog, onClose, onSaved }) {
  const isEdit = dialog.mode === "edit";
  const [form, setForm] = useState({
    name: dialog.user?.name || "", email: dialog.user?.email || "",
    role: dialog.user?.role || "user", password: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${dialog.user.id}`, payload);
      } else {
        if (!form.email || !form.password || !form.name) { toast.error("Tüm alanlar zorunludur"); setSaving(false); return; }
        await api.post("/users", form);
      }
      toast.success(isEdit ? "Güncellendi" : "Kullanıcı eklendi");
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
          <DialogDescription>{isEdit ? "Şifreyi değiştirmek için yeni bir şifre girin." : "Yeni bir kullanıcı hesabı oluşturun."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={isEdit} data-testid="user-email-input" /></div>
          <div className="space-y-1.5">
            <Label>Rol</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Kullanıcı</SelectItem>
                <SelectItem value="admin">Yönetici</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>{isEdit ? "Yeni Şifre (opsiyonel)" : "Şifre"}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="user-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
